import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react-router";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSettings } from "@/contexts/SettingsContext";
import { formatCurrency, formatDate } from "@/lib/formatters";
import { getAuthHeaders } from "@/lib/api";
import { setMetaDescription, setCanonicalUrl, setOpenGraphTags, setTwitterCardTags, setMetaRobots } from "@/lib/seo";
import { generateWebPageSchema, generateBreadcrumbListSchema, addSchemaToPage } from "@/lib/schema";
import { Pencil, Trash2, Info } from "lucide-react";
import { ClickableTooltip } from "@/components/ui/tooltip";

type Expense = {
  id: number;
  amount: number;
  category: string;
  date: string;
  frequency?: string;
  is_future?: number;
};

export default function ExpensesPage() {
  useEffect(() => {
    const title = "Expenses - Munthly";
    const description = "Organise your expenses and bills with Munthly. Track regular bills, provisions, and future expenses to create a stable monthly balance sheet.";
    
    document.title = title;
    setMetaDescription(description);
    const canonicalCleanup = setCanonicalUrl("/expenses");
    const ogCleanup = setOpenGraphTags(title, description, "/expenses");
    const twitterCleanup = setTwitterCardTags(title, description);
    const robotsCleanup = setMetaRobots("noindex, nofollow"); // Protected page - don't index
    
    // Add schema for expenses page
    const webPageSchema = generateWebPageSchema(
      "/expenses",
      title,
      description
    );
    const breadcrumbSchema = generateBreadcrumbListSchema([
      { name: "Home", url: "/" },
      { name: "Dashboard", url: "/dashboard" },
      { name: "Expenses", url: "/expenses" }
    ]);
    
    const schemaCleanup = addSchemaToPage([webPageSchema, breadcrumbSchema]);
    
    return () => {
      canonicalCleanup();
      ogCleanup();
      twitterCleanup();
      robotsCleanup();
      schemaCleanup();
    };
  }, []);
  const [expenses, setExpenses] = useState<Expense[]>([]);

  // Form State
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [frequency, setFrequency] = useState("Monthly");

  // UX State: "recurring" | "future"
  const [mode, setMode] = useState<"recurring" | "future">("recurring");

  // State for Editing
  const [editingId, setEditingId] = useState<number | null>(null);

  const { settings } = useSettings();
  const { getToken } = useAuth();

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    const res = await fetch("/api/v1/expenses", {
      headers: await getAuthHeaders(getToken)
    });
    const data = await res.json();
    setExpenses(data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !category || !date) return;

    const isFuture = mode === "future";
    const finalFrequency = mode === "recurring" ? frequency : null;

    if (editingId) {
      // Update existing
      const res = await fetch(`/api/v1/expenses/${editingId}`, {
        method: "PUT",
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify({
          amount: parseFloat(amount),
          category,
          date,
          frequency: finalFrequency,
          is_future: isFuture
        }),
      });
      if (!res.ok) {
        alert("Failed to update expense");
        return;
      }
      setEditingId(null);
    } else {
      // Create new
      const res = await fetch("/api/v1/expenses", {
        method: "POST",
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify({
          amount: parseFloat(amount),
          category,
          date,
          frequency: finalFrequency,
          is_future: isFuture
        }),
      });
      if (!res.ok) {
        alert("Failed to add expense");
        return;
      }
    }

    // Reset basics but keep mode/date
    setAmount("");
    setCategory("");
    fetchExpenses();
  };

  const handleEdit = (exp: Expense) => {
    setCategory(exp.category);
    setAmount(exp.amount.toString());
    setDate(exp.date);
    setEditingId(exp.id);

    // Set Mode and Frequency based on expense type
    if (exp.is_future) {
      setMode("future");
      setFrequency("Monthly"); // Default fallback
    } else {
      setMode("recurring");
      setFrequency(exp.frequency || "Monthly");
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setCategory("");
    setAmount("");
    setEditingId(null);
    // Optional: reset mode? Keep as is implies user might want to add similar items.
  }

  const handleDelete = async (id: number) => {
    await fetch(`/api/v1/expenses/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    fetchExpenses();
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-heading text-2xl sm:text-3xl font-semibold tracking-tight">Expenses</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your expenses and provisions</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle>{editingId ? "Edit Expense" : "Add New Expense"}</CardTitle>
            <ClickableTooltip
              content={
                <div className="space-y-2">
                  <p><strong>Regular Expenses:</strong> Track your recurring bills and commitments. Add the amount and date they occur, and Munthly will include them in your monthly balance sheet.</p>
                  <p><strong>Provisions:</strong> Plan for one-off future costs. Add the total amount and expected date, and Munthly will automatically calculate how much to set aside each month leading up to it.</p>
                </div>
              }
            >
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label="How expenses work"
              >
                <Info className="w-4 h-4" />
              </button>
            </ClickableTooltip>
          </div>
          <CardDescription>
            {mode === "recurring"
              ? "Track your regular bills and commitments."
              : "Plan for one-off costs. We'll break them down monthly for you."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Toggle Modes */}
          <div className="flex gap-2 mb-6 pb-4">
            <Button
              variant={mode === "recurring" ? "default" : "outline"}
              onClick={() => setMode("recurring")}
              className="rounded-full flex-1"
            >
              Regular
            </Button>
            <Button
              variant={mode === "future" ? "default" : "outline"}
              onClick={() => setMode("future")}
              className="rounded-full flex-1"
            >
              Provision
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Input
                  type="text"
                  id="category"
                  placeholder="e.g. Rent, Groceries, Holiday"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount</Label>
                <Input
                  type="number"
                  id="amount"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">
                  {mode === "recurring" ? "Start Date" : "Expected Date"}
                </Label>
                <Input
                  type="date"
                  id="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              {mode === "recurring" && (
                <div className="space-y-2 sm:col-span-2 lg:col-span-1">
                  <Label htmlFor="frequency">Frequency</Label>
                  <Select value={frequency} onValueChange={setFrequency}>
                    <SelectTrigger className="w-full h-12 rounded-2xl">
                      <SelectValue placeholder="Frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Weekly">Weekly</SelectItem>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end pt-2">
              {editingId && (
                <Button type="button" variant="outline" onClick={handleCancel}>Cancel</Button>
              )}
              <Button type="submit" disabled={!amount || !category || !date}>
                {editingId ? "Update Expense" : (mode === "recurring" ? "Add Regular Expense" : "Add Provision")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Expenses History</CardTitle>
        </CardHeader>
        <CardContent>
          {expenses.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No expenses recorded yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {expenses.map((exp) => (
                <div key={exp.id} className="flex justify-between items-center p-4 rounded-2xl bg-[var(--surface-container)] hover:bg-[var(--surface-container-high)] transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-base">{exp.category}</p>
                      {/* Type badge */}
                      {exp.is_future === 1 ? (
                        <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 px-2.5 py-1 rounded-full font-medium">Future</span>
                      ) : exp.frequency === "Annual" || exp.frequency === "Yearly" ? (
                        <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-2.5 py-1 rounded-full font-medium">Annual</span>
                      ) : (
                        <span className="text-xs bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300 px-2.5 py-1 rounded-full font-medium">Regular</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">{formatDate(exp.date, settings.dateFormat)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-lg" style={{ color: 'var(--destructive)' }}>
                      -{formatCurrency(exp.amount, settings.currencySymbol)}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon-sm" onClick={() => handleEdit(exp)}>
                        <Pencil className="w-4 h-4" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      <Button variant="ghost" size="icon-sm" onClick={() => handleDelete(exp.id)} className="text-destructive hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
